/**
 * Waking and putting to sleep a permanent environment (plan « branches
 * protégées en sommeil », lot 4b): who is offered which button, and the
 * launch of the two templates — those the project's AppType designates
 * (`env-wake`, `env-sleep` for Odoo), the caller passing the ref —,
 * run by the backend actions `waadoo:env:wake` and `waadoo:env:sleep`
 * (`scaffolder/actions/environmentPower/`).
 *
 * UX ONLY: the buttons follow the rights the actions enforce server-side
 * (their `common.ts`) — the project's editors and the admin tier, the
 * production for the admin tier alone, nobody else. The caller's level is
 * the one of the « Lecture seule » badge (`projectAccess.ts`), read from
 * their own groups; a button shown wrongly is refused by the task.
 *
 * The launch goes through the scaffolder API (`scaffold`), the way the
 * template's own form would, then the caller is taken to the task's page —
 * the environment task page of `taskProjectBanner/`.
 */

import type { ScaffolderApi } from '@backstage/plugin-scaffolder-react';
import { branchKind, isEphemeralBranch } from './branchKind';
import type { ProjectAccessLevel } from './projectAccess';

/**
 * The environment every project has. Its sleep is refused by the backend in
 * this lot: `dev/` of the appenv also deploys the ephemeral branches'
 * ApplicationSet, which its sleep would delete with them.
 */
export const DEV_BRANCH = 'dev';

/**
 * What the caller gets on one environment: the button (`allowed`), the
 * button locked with « Réservé aux administrateurs » (`locked`: an editor on
 * the production), or nothing (`hidden`: a plain member, an unknown level).
 */
export type PowerRight = 'allowed' | 'locked' | 'hidden';

export function powerRight(
  level: ProjectAccessLevel | undefined,
  branch: string,
): PowerRight {
  if (level === 'admin') {
    return 'allowed';
  }
  if (level === 'edit') {
    return branchKind(branch) === 'production' ? 'locked' : 'allowed';
  }
  return 'hidden';
}

/** Whether « Mettre en sommeil » is offered at all for a deployed branch. */
export function canBePutToSleep(branch: string): boolean {
  return !isEphemeralBranch(branch) && branch !== DEV_BRANCH;
}

/** What the caller types to confirm: `<project>/<branch>`. */
export function confirmationText(project: string, branch: string): string {
  return `${project}/${branch}`;
}

/** Whether the wake of `branch` asks for the typed confirmation. */
export function wakeNeedsConfirmation(branch: string): boolean {
  return branchKind(branch) === 'production';
}

/** The data a woken environment starts from (the template's `data`). */
export type WakeData = 'latest' | 'fresh';

/** Starts the wake; resolves to the task's id. */
export async function launchWake(
  scaffolderApi: Pick<ScaffolderApi, 'scaffold'>,
  options: {
    /**
     * The template to run: the project's own for this role
     * (`useProjectTemplates`, components/shared/templateRoles.ts).
     */
    templateRef: string;
    project: string;
    branch: string;
    data: WakeData;
    confirmation?: string;
  },
): Promise<string> {
  const { taskId } = await scaffolderApi.scaffold({
    templateRef: options.templateRef,
    values: {
      project: options.project,
      environment: options.branch,
      data: options.data,
      ...(options.confirmation !== undefined
        ? { confirmation: options.confirmation }
        : {}),
    },
  });
  return taskId;
}

/** Starts the sleep; resolves to the task's id. */
export async function launchSleep(
  scaffolderApi: Pick<ScaffolderApi, 'scaffold'>,
  options: {
    /**
     * The template to run: the project's own for this role
     * (`useProjectTemplates`, components/shared/templateRoles.ts).
     */
    templateRef: string;
    project: string;
    branch: string;
    confirmation: string;
  },
): Promise<string> {
  const { taskId } = await scaffolderApi.scaffold({
    templateRef: options.templateRef,
    values: {
      project: options.project,
      environment: options.branch,
      confirmation: options.confirmation,
    },
  });
  return taskId;
}
